import React from 'react';
import { FormattedMessage } from 'react-intl';

import { routes } from '@/config';
import { useLoginUrl } from '@/hooks';

import { SubmitConfirmationTrackingLo } from './submit-confirmation-tracking-lo';
import { AccountState, AccountStateType } from './types';

import * as S from './xing-account-confirmation-variants.styles';

type XingAccountConfirmationProps = {
  accountState: AccountStateType;
  hashedUserId: string;
};

const ICON_IMAGE_SIZE = 16;

export const XingAccountConfirmationVariants: React.VFC<XingAccountConfirmationProps> = ({
  accountState,
  hashedUserId,
}) => {
  const loginUrl = useLoginUrl({ extraParams: { sc_o: 'jobs_apply_complete_login' } });
  const onRegisterClick = (): void =>
    window.location.replace(
      `${routes.signup}?sc_o=jobs_apply_complete_register&signup_channel=jobs_apply_form_manual_profile_creation`
    );

  switch (accountState) {
    case AccountState.NO_USER:
      return (
        <S.VariantsContainer>
          <SubmitConfirmationTrackingLo
            confirmationTrackingBaseData={{
              PropContextDimension1: 'jobs_apply_complete_user_new',
              EventRegistrationUnconfirmed: 1,
              PropHashedUserId: hashedUserId,
              PropRegistrationBackendChannel: 'jobs_apply_form_auto_profile_creation',
            }}
          />
          <S.VariantTitle>
            <S.StyledIconTick width={ICON_IMAGE_SIZE} height={ICON_IMAGE_SIZE} />
            <span>
              <FormattedMessage id="JOBS_INSTANT_APPLY_PROFILE_CREATED_BY_XING_TITLE" />
            </span>
          </S.VariantTitle>
          <S.VariantText>
            <FormattedMessage id="JOBS_INSTANT_APPLY_PROFILE_CREATED_BY_XING_TEXT" />
          </S.VariantText>
        </S.VariantsContainer>
      );
    case AccountState.EXISTS:
      return (
        <S.VariantsContainer>
          <SubmitConfirmationTrackingLo
            confirmationTrackingBaseData={{
              PropContextDimension1: 'jobs_apply_complete_user_registered',
            }}
          />
          <S.VariantTitle>
            <S.StyledIconTick width={ICON_IMAGE_SIZE} height={ICON_IMAGE_SIZE} />
            <span>
              <FormattedMessage id="JOBS_INSTANT_APPLY_PROFILE_CONFIRMED_ACCOUNT_TITLE" />
            </span>
          </S.VariantTitle>
          <S.VariantText>
            <FormattedMessage
              id="JOBS_INSTANT_APPLY_PROFILE_CONFIRMED_ACCOUNT_TEXT"
              values={{
                a: (chunks) => <a href={loginUrl}>{chunks}</a>,
              }}
            />
          </S.VariantText>
        </S.VariantsContainer>
      );
    default:
      return (
        <S.VariantsContainer>
          <SubmitConfirmationTrackingLo
            confirmationTrackingBaseData={{
              PropContextDimension1: 'jobs_apply_complete_user_error',
            }}
          />
          <S.VariantTitle>
            <S.StyledIconInfo width={ICON_IMAGE_SIZE} height={ICON_IMAGE_SIZE} />
            <span>
              <FormattedMessage id="JOBS_INSTANT_APPLY_PROFILE_ERROR_TITLE" />
            </span>
          </S.VariantTitle>
          <S.VariantText>
            <FormattedMessage
              id="JOBS_INSTANT_APPLY_PROFILE_ERROR_TEXT"
              values={{
                p: (chunks) => <span>{chunks}</span>,
                a: (chunks) => <a onClick={onRegisterClick}>{chunks}</a>,
              }}
            />
          </S.VariantText>
        </S.VariantsContainer>
      );
  }
};
